import { useTranslation, untranslated } from "@i18n";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import EditIcon from "@Components/StickySidebarViewer/assets/EditIcon";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { updateGroupsData } from "@Actions/portalSetting";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getBackendUrl } from "@Utils";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { renderToStaticMarkup } from "react-dom/server";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { useNavigate } from "react-router-dom";
import { getGroupDetailsUrl } from "@Services/utils/helpers";
import IconComponent from "@CubeComponents/icons";
import { GridPageLayout } from "@CubeComponents/html/GridPageLayout";
import type { RootState } from "@Reducers/index";

function Groups(props: any) {
  const { t } = useTranslation();
  const dataGrid: any = useRef(null);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isDataEmpty, setIsDataEmpty] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialGroupValues = {
    name: "",
    description: "",
  };
  const [GroupDetailDataToSave, setGroupDetailDataToSave] = useState(initialGroupValues);
  const [toolbarButtonState, setToolbarButtonState] = useState({
    edit: true,
    delete: true,
  });

  const remoteDataSource = `${getBackendUrl()}/groups/updateGroupsData`;
  const query = useMemo(() => ({}), []);

  const { portalSettingInfo } = useSelector((state: RootState) => state);

  const createBtnTemplate = () => (
    <div>
      <ButtonComponent id="custom_add" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
        <span className="flex items-center justify-start gap-4">
          <CreateIcon size="15" className="" />
          <Text>{t("Create")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const editBtnTemplate = () => (
    <div>
      <ButtonComponent id="custom_edit" buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled={toolbarButtonState.edit}>
        <span className="flex items-center justify-start gap-4">
          <EditIcon size="15" className="" />
          <Text>{t("Edit")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const deleteBtnTemplate = () => (
    <div>
      <ButtonComponent id="custom_delete" buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled={toolbarButtonState.delete}>
        <span className="flex items-center justify-start gap-4">
          <DeleteIcon size="15" className="" />
          <Text>{t("Delete")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const addEditModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="30%"
      headerTitle={headerContent()}
      onCancel={handleAddEditModalCLose}
      isVisible={openAddEditModal}
      modelContent={modelContent()}
      headerContent={untranslated("")}
    />
  );

  const deleteGroup = async () => {
    const rowData = dataGrid.current?.dataView?.getAllSelectedItems() || [];
    const dataToDelete = rowData.map((obj: any) => ({ _id: obj._id }));

    const data = {
      data: dataToDelete,
      action: "delete" as const,
    };
    await updateGroupsData(data);
    setOpenDeleteModal(false);
    dataGrid.current?.refresh();
  };

  const deleteModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex gap-4 justify-normal">
          <ButtonComponent buttonType="gradientButton" onClick={deleteGroup}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType="borderButton" onClick={handleAddEditModalCLose}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      width="30%"
      headerTitle={t("Delete Group")}
      onCancel={handleAddEditModalCLose}
      isVisible={openDeleteModal}
      modelContent={
        <div className="flex flex-col justify-center w-full h-full">
          <Text className="mb-4">{t("Are you sure you want to delete the selected group(s)?")}</Text>
          <Text className="font-bold text-wrap">
            {t("Note:- Deleting the group(s) will delete it from all entities but keep the users.")}
          </Text>
        </div>
      }
    />
  );

  const footerContent = () => (
    <div className="">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleCreateGroupClick}
        disabled={!GroupDetailDataToSave.name}
      >
        {editMode ? t("Save") : t("Create")}
      </ButtonComponent>
    </div>
  );

  const handleCreateGroupClick = async () => {
    const selectedData = editMode ? dataGrid.current?.dataView?.getAllSelectedItems()?.[0] || {} : {};
    const data = {
      data: { ...GroupDetailDataToSave, groupId: selectedData._id },
      action: (editMode ? "update" : "insert") as "update" | "insert",
    };
    const res = await updateGroupsData(data);
    if (res.data.status) {
      const content = editMode
        ? t("The changes have been saved successfully.")
        : t('New Group "{{name}}" has been created successfully', { name: GroupDetailDataToSave.name });
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setGroupDetailDataToSave(initialGroupValues);
      handleAddEditModalCLose();
      if (dataGrid.current) {
        dataGrid.current.refresh();
      } else {
        setIsDataEmpty(false);
      }
    }
  };

  const handleChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    if (args?.target) {
      const data = args.target.value;
      setGroupDetailDataToSave({
        ...GroupDetailDataToSave,
        [args.target.name]: data,
      });
    }
  };

  const modelContent = () => (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-1">
        <Text>{t("Group Name")}</Text>
        <span>
          <TextFieldComponent
            name="name"
            type="text"
            placeholder={t("Add Group Name")}
            showClearButton
            onChange={handleChange}
            value={GroupDetailDataToSave.name}
          />
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <Text>{t("Group Description (Optional)")}</Text>
        <span>
          <TextFieldComponent
            name="description"
            type="text"
            placeholder={t("Add Group Description ")}
            showClearButton
            onChange={handleChange}
            value={GroupDetailDataToSave.description}
          />
        </span>
      </div>
    </div>
  );

  const handleAddEditModalCLose = () => {
    setOpenAddEditModal(false);
    setOpenDeleteModal(false);
    setEditMode(false);
    setGroupDetailDataToSave(initialGroupValues);
  };

  const headerContent = () => (editMode ? t("Edit Group") : t("Create Group"));

  function NoGroups() {
    return (
      <div className="w-full h-full overflow-hidden">
        <div className="w-full h-full overflow-hidden flex gap-2 px-2">
          <div className="w-full overflow-hidden flex flex-col">
            <div className="flex flex-col h-full w-full justify-center items-center bg-white p-5">
              <IconComponent name="InspectionFormNoData" width="15rem" height="15rem" />
              <div className="mt-5 text-sm">
                <Text className="font-semibold">{t("Create")}</Text>
                <Text> {t("a group to get started.")}</Text>
              </div>
              <div className="flex m-5 gap-5">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={() => setOpenAddEditModal(true)}
                  disabled={!portalSettingInfo?.groups?.addGroups?.enable}
                >
                  <span className="flex items-center justify-start">
                    <CreateIcon size="15" className="me-5" />
                    <Text>{t("Create")}</Text>
                  </span>
                </ButtonComponent>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onClick = (e: any) => {
    const args = e?.detail?.args;
    if (!args) return;
    const rowData = dataGrid.current?.dataView?.getItemByIdx(args.row);
    if (rowData && args.cell !== 0) {
      props.setGroupData(rowData);
      navigate(getGroupDetailsUrl(rowData._id));
    }
  };

  useEffect(() => {
    document.title = t("Portal Settings | Groups");
  }, []);

  const beforeDataBound = (response: any) => {
    const data = response?.data?.result || [];
    if (!data.length) {
      setIsDataEmpty(true);
    } else {
      setIsDataEmpty(false);
    }
    return data;
  };

  const toolbarClickHandler = (toolClicked: string) => {
    switch (toolClicked) {
      case "custom_add":
        setOpenAddEditModal(true);
        break;

      case "custom_edit": {
        const selectedData = dataGrid.current?.dataView?.getAllSelectedItems()?.[0];
        if (selectedData) {
          setGroupDetailDataToSave({
            name: selectedData.name,
            description: selectedData.description,
          });
          setOpenAddEditModal(true);
          setEditMode(true);
        }
        break;
      }

      case "custom_delete":
        setOpenDeleteModal(true);
        break;
    }
  };

  const toolbarOptions = [
    {
      id: "custom_add",
      name: t("Create"),
      template: createBtnTemplate,
      disabled: !portalSettingInfo?.groups?.addGroups?.enable,
    },
    {
      id: "custom_edit",
      name: t("Edit"),
      template: editBtnTemplate,
      disabled: toolbarButtonState.edit || !portalSettingInfo?.groups?.editGroups?.enable,
    },
    {
      id: "custom_delete",
      name: t("Delete"),
      template: deleteBtnTemplate,
      disabled: toolbarButtonState.delete || !portalSettingInfo?.groups?.deleteGroups?.enable,
    },
  ];

  const onSelectedRowIdsChanged = (args: any) => {
    const selectedIds = args?.detail?.args?.selectedRowIds || [];
    const totalRowsSelected = selectedIds.length;

    if (totalRowsSelected === 0) {
      setToolbarButtonState({ edit: true, delete: true });
    } else if (totalRowsSelected === 1) {
      setToolbarButtonState({ edit: false, delete: false });
    } else {
      setToolbarButtonState({ edit: true, delete: false });
    }
  };

  const nameHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Name")} />);
  const descriptionHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Multi Line" label={t("Description")} />);
  const modifiedOnHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Date and Time" label={t("Modified On")} />);
  const modifiedByHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="User Profile" label={t("Modified by")} />);

  const columnDefinitions = [
    {
      id: "_id",
      field: "_id",
      name: "_id",
      hidden: true,
    },
    {
      id: "name",
      field: "name",
      name: t("Name"),
      width: 180,
      originalWidth: 180,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      headerRenderer: nameHeaderRenderer,
    },
    {
      id: "description",
      field: "description",
      name: t("Description"),
      width: 280,
      originalWidth: 280,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      headerRenderer: descriptionHeaderRenderer,
    },
    {
      id: "updatedDate",
      field: "updatedDate",
      name: t("Modified On"),
      width: 180,
      originalWidth: 180,
      formatterType: "Date",
      filterType: "Date",
      clipMode: "EllipsisWithTooltip",
      headerRenderer: modifiedOnHeaderRenderer,
    },
    {
      id: "updatedBy",
      field: "updatedBy.name",
      name: t("Modified by"),
      width: 180,
      originalWidth: 180,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      _valueGetter: (args: any) => args?.updatedBy?.name || "-",
      headerRenderer: modifiedByHeaderRenderer,
    },
  ];

  const getGroupsList = () => (
    <SlickGrid1
      ref={dataGrid}
      columnDefinitions={columnDefinitions as any}
      remoteDataSource={remoteDataSource}
      query={query}
      beforeDataBound={beforeDataBound}
      toolbar={toolbarOptions}
      toolbarClickHandler={toolbarClickHandler}
      onSelectedRowIdsChanged={onSelectedRowIdsChanged}
      onClick={onClick}
      enableFilteringOption
      enableSortingOption
      enableGroupingOption
      enableColumnChooser
      enableRowResize
      rowHeight={68 as any}
      enableMultiSelect={false}
      excelExport
    />
  );

  return (
    <>
      {!props.groupProperties ? (
        <GridPageLayout
          showHeader={!props.groupProperties}
          title="Groups"
          description="List of all the groups created in this portal"
        >
          {!isDataEmpty ? getGroupsList() : NoGroups()}
        </GridPageLayout>
      ) : null}
      {openAddEditModal ? addEditModal() : null}
      {openDeleteModal ? deleteModal() : null}
    </>
  );
}

export default Groups;
